import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";

import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import LoadingSpinner from "../../components/LoadingSpinner";

import toast from "react-hot-toast";
import { useAuth } from "../../providers/AuthContext";

import { CreatableSelectRow, InputRow, SwitchRow, TextAreaRow } from "../../components/Rows";
import EntityMultiSelectForm from "../../components/EntityMultiSelectForm";
import { FormCard, FormGrid } from "../../components/FormCard";

import {
    getOrganizationByPublicId,
    updateOrganization,
} from "../../api/Resources";

import { getAllChargingSites } from "../../api/ChargingSites";

const splitEmails = (value) =>
    String(value ?? "")
        .split(";")
        .map((email) => email.trim())
        .filter(Boolean);

const joinEmails = (values) =>
    (values || [])
        .map((email) => String(email ?? "").trim())
        .filter(Boolean)
        .join(";");

const EditOrganizationPage = () => {
    const { t } = useTranslation();
    const { getAccessToken } = useAuth();
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();

    const query = new URLSearchParams(location.search);
    const [activeTab, setActiveTab] = useState(query.get("tab") || "details");

    const [loading, setLoading] = useState(true);

    const [org, setOrg] = useState({
        organizationPublicId: "",
        organizationName: "",
        companyName: "",
        contactMail: "",
        supportPhone: "",
        website: "",
        address: "",
        email: [],
        isDefault: false,
        isCpms: false,
    });

    const [originalOrg, setOriginalOrg] = useState(null);

    const [selectedChargingSiteIds, setSelectedChargingSiteIds] = useState([]);
    const [originalChargingSiteIds, setOriginalChargingSiteIds] = useState([]);

    const [hasChanges, setHasChanges] = useState(false);
    const [supportPhoneInvalid, setSupportPhoneInvalid] = useState(false);
    const [supportEmailInvalid, setSupportEmailInvalid] = useState(false);

    // Sync tab i URL
    useEffect(() => {
        const q = new URLSearchParams(location.search);
        q.set("tab", activeTab);
        navigate(`${location.pathname}?${q.toString()}`, { replace: true });
    }, [activeTab, location.pathname, location.search, navigate]);

    // Load org
    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const token = await getAccessToken();
                const o = await getOrganizationByPublicId(id, token);

                const normalized = {
                    organizationPublicId:
                        o?.organizationPublicId ?? o?.publicId ?? id ?? "",
                    organizationName: o?.organizationName ?? o?.name ?? "",
                    companyName: o?.companyName ?? "",
                    contactMail: o?.contactMail ?? "",
                    supportPhone: o?.supportPhone ?? "",
                    website: o?.website ?? "",
                    address: o?.address ?? "",
                    email: splitEmails(o?.email ?? ""),
                    isDefault: o?.isDefault === true,
                    isCpms: o?.isCpms === true,
                };

                setOrg(normalized);
                setOriginalOrg(normalized);

                const ids =
                    (o?.chargingSites || o?.chargingSitePublicIds || [])
                        .map((x) => (typeof x === "string" ? x : x?.chargingSitePublicId))
                        .filter(Boolean) || [];

                setSelectedChargingSiteIds(ids);
                setOriginalChargingSiteIds(ids);
            } catch (err) {
                console.error(err);
                toast.error(t("toast.failedLoad", { entity: t("entity.organization") }));
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [getAccessToken, id]);

    // hasChanges
    useEffect(() => {
        if (!originalOrg) return;

        const orgChanged =
            org.organizationName !== originalOrg.organizationName ||
            org.companyName !== originalOrg.companyName ||
            org.contactMail !== originalOrg.contactMail ||
            org.supportPhone !== originalOrg.supportPhone ||
            org.website !== originalOrg.website ||
            org.address !== originalOrg.address ||
            org.isCpms !== originalOrg.isCpms ||
            JSON.stringify([...(org.email || [])].sort()) !==
                JSON.stringify([...(originalOrg.email || [])].sort());

        const normalize = (arr) => [...(arr || [])].sort();
        const sitesChanged =
            org.isCpms !== true &&
            JSON.stringify(normalize(selectedChargingSiteIds)) !==
            JSON.stringify(normalize(originalChargingSiteIds));

        setHasChanges(orgChanged || sitesChanged);
    }, [org, originalOrg, selectedChargingSiteIds, originalChargingSiteIds]);

    const navigateBack = () => {
        navigate("/organizations");
    };

    const validate = () => {
        if (!org.organizationName.trim()) {
            toast.error(t("common.nameRequired"));
            return false;
        }
        const phoneOk = Boolean(org.supportPhone.trim());
        const emailOk = (org.email || []).some((email) => String(email ?? "").trim());
        setSupportPhoneInvalid(!phoneOk);
        setSupportEmailInvalid(!emailOk);
        if (!phoneOk || !emailOk) {
            toast.error(t("common.pleaseFillRequired"));
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        if (!validate()) return;

        try {
            const token = await getAccessToken();

            // UpdateOrganizationDto
            const payload = {
                organizationPublicId: org.organizationPublicId || id,
                organizationName: org.organizationName.trim(),
                companyName: org.companyName?.trim() || null,
                contactMail: org.contactMail?.trim() || null,
                supportPhone: org.supportPhone.trim(),
                website: org.website?.trim() || null,
                address: org.address?.trim() || null,
                email: joinEmails(org.email) || null,
                isCpms: org.isCpms === true,
            };

            if (org.isCpms !== true) {
                payload.chargingSitePublicIds = (selectedChargingSiteIds || []).filter(Boolean);
            }

            await updateOrganization(payload, token);

            setOriginalOrg({ ...org });
            setOriginalChargingSiteIds([...(selectedChargingSiteIds || [])]);

            toast.success(t("toast.updated", { entity: t("entity.organization") }));
        } catch (err) {
            console.error(err);
            toast.error(err?.message || t("toast.failedUpdate", { entity: t("entity.organization") }));
        }
    };

    const handleInputChange = (key, value) => {
        if (key === "supportPhone") {
            setSupportPhoneInvalid(false);
        }
        if (key === "email") {
            setSupportEmailInvalid(false);
        }
        setOrg((prev) => ({ ...prev, [key]: value }));
    };

    if (loading) {
        return (
            <div className="min-h-screen w-full flex items-center justify-center">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <>
            <HeaderBar />
            <div className="flex flex-row w-full">
                <Sidebar />
                <main className="page">
                    <div className={`${loading ? "loading" : ""} page-content`}>
                        <div className="flex flex-row items-center pb-4">
                            <button onClick={navigateBack} className="text-black flex">
                                <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
                            </button>

                            <h2 className="border-l border-mute-200 pl-4">
                                {org.organizationName || t("entity.organization")}
                            </h2>

                            <div className="flex gap-4 ml-auto">
                                <button className="btn btn-outline-primary" onClick={navigateBack}>
                                    {t("common.cancel")}
                                </button>
                                <button
                                    className="btn btn-primary"
                                    disabled={!hasChanges || loading}
                                    onClick={handleSave}
                                >
                                    {t("common.save")}
                                </button>
                            </div>
                        </div>

                        <div className="px-4 py-8 shadow-lg rounded-md w-full">
                            <div className="border-b border-mute-200 mb-4 gap-4 flex">
                                <button
                                    className={`px-4 pb-2 ${activeTab === "details"
                                            ? "border-b-2 border-black font-bold text-sm text-black"
                                            : "text-mute-400 text-sm font-bold"
                                        }`}
                                    onClick={() => setActiveTab("details")}
                                >
                                    {t("tabs.details")}
                                </button>
                            </div>

                            {activeTab === "details" && (
                                <div className="flex flex-col gap-8">
                                    {/* Details */}
                                    <FormCard >
                                        <FormGrid>
                                            {org.isDefault !== true && (
                                                <SwitchRow
                                                    label={t("form.cpmsOrganization")}
                                                    checked={org.isCpms === true}
                                                    onChange={(checked) => handleInputChange("isCpms", checked)}
                                                />
                                            )}

                                            <InputRow
                                                label={t("form.organizationName")}
                                                value={org.organizationName}
                                                onChange={(v) => handleInputChange("organizationName", v)}
                                                invalid={false}
                                                maxLength={50}
                                            />

                                            <InputRow
                                                label={t("form.companyName")}
                                                value={org.companyName}
                                                onChange={(v) => handleInputChange("companyName", v)}
                                                invalid={false}
                                                maxLength={200}
                                            />

                                            <InputRow
                                                label={t("form.contactMail")}
                                                value={org.contactMail}
                                                onChange={(v) => handleInputChange("contactMail", v)}
                                                invalid={false}
                                                maxLength={255}
                                            />

                                            <InputRow
                                                label={t("form.supportPhone")}
                                                value={org.supportPhone}
                                                onChange={(v) => handleInputChange("supportPhone", v)}
                                                invalid={supportPhoneInvalid}
                                                maxLength={40}
                                            />

                                            <InputRow
                                                label={t("common.website")}
                                                value={org.website}
                                                onChange={(v) => handleInputChange("website", v)}
                                                invalid={false}
                                                maxLength={200}
                                            />

                                            <CreatableSelectRow
                                                label={t("form.supportEmails")}
                                                value={org.email}
                                                options={(org.email || []).map((email) => ({
                                                    label: email,
                                                    value: email,
                                                }))}
                                                onChange={(v) =>
                                                    handleInputChange("email", v || [])
                                                }
                                                invalid={supportEmailInvalid}
                                                isMulti={true}
                                                placeholder={t("form.typeSupportEmail")}
                                            />

                                            <TextAreaRow
                                                label={t("common.address")}
                                                value={org.address}
                                                onChange={(v) => handleInputChange("address", v)}
                                                maxLength={300}
                                                rows={4}
                                            />
                                        </FormGrid>
                                    </FormCard>

                                    {org.isCpms !== true && (
                                        <EntityMultiSelectForm
                                            title={t("form.chargingSites")}
                                            fetchItems={async (token) => {
                                                const list = await getAllChargingSites(token);
                                                return (list || []).map((s) => ({
                                                    ...s,
                                                    chargingSitePublicId:
                                                        s?.chargingSitePublicId ?? s?.publicId ?? s?.id,
                                                    chargingSiteName: s?.chargingSiteName ?? s?.name ?? "",
                                                }));
                                            }}
                                            labelKey="chargingSiteName"
                                            valueKey="chargingSitePublicId"
                                            selectedIds={selectedChargingSiteIds}
                                            onChange={(ids) =>
                                                setSelectedChargingSiteIds((ids || []).filter(Boolean))
                                            }
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </main>
            </div>
        </>
    );
};

export default EditOrganizationPage;
